/**
 * Hooks for live-subscribed invoice data.
 *
 * Pattern: each hook returns `{ status, data, error }` for clean rendering.
 * The Firestore queries match the indexes deployed in Batch 2.
 */

import { useEffect, useState } from 'react';
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import { db } from '../../lib/firebase';
import type { InvoiceSubmission } from '@daaily/shared';

const COLLECTION = 'invoiceSubmissions';

type Status = 'loading' | 'ready' | 'error';

export interface ListResult {
  status: Status;
  invoices: InvoiceSubmission[];
  error?: string;
}

export interface DocResult {
  status: Status;
  invoice: InvoiceSubmission | null;
  error?: string;
}

/** Live-subscribe to all invoices for a given submitter, newest first. */
export function useMyInvoices(email: string | null): ListResult {
  const [state, setState] = useState<ListResult>({
    status: 'loading',
    invoices: [],
  });

  useEffect(() => {
    if (!email) {
      setState({ status: 'loading', invoices: [] });
      return;
    }
    const q = query(
      collection(db, COLLECTION),
      where('submitterEmail', '==', email.toLowerCase()),
      orderBy('createdAt', 'desc'),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const invoices: InvoiceSubmission[] = [];
        snap.forEach((d) => {
          invoices.push({ ...(d.data() as InvoiceSubmission), id: d.id });
        });
        setState({ status: 'ready', invoices });
      },
      (err) => {
        // eslint-disable-next-line no-console
        console.error('[useMyInvoices] snapshot error:', err);
        setState({ status: 'error', invoices: [], error: err.message });
      },
    );
    return unsub;
  }, [email]);

  return state;
}

/** Live-subscribe to a single invoice doc by id. */
export function useInvoice(invoiceId: string | null): DocResult {
  const [state, setState] = useState<DocResult>({
    status: 'loading',
    invoice: null,
  });

  useEffect(() => {
    if (!invoiceId) {
      setState({ status: 'loading', invoice: null });
      return;
    }
    const ref = doc(db, COLLECTION, invoiceId);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          setState({ status: 'ready', invoice: null });
        } else {
          setState({
            status: 'ready',
            invoice: { ...(snap.data() as InvoiceSubmission), id: snap.id },
          });
        }
      },
      (err) => {
        // eslint-disable-next-line no-console
        console.error('[useInvoice] snapshot error:', err);
        setState({ status: 'error', invoice: null, error: err.message });
      },
    );
    return unsub;
  }, [invoiceId]);

  return state;
}

/**
 * Live-subscribe to all submitted invoices (queue view).
 * Oldest first (FIFO).
 *
 * Kept for backward compatibility; new code uses `useInvoicesByStatus`.
 */
export function useSubmittedInvoices(): ListResult {
  return useInvoicesByStatus('submitted');
}

/** Filter values accepted by useInvoicesByStatus. */
export type ApprovalFilter = 'submitted' | 'approved' | 'rejected' | 'all';

/**
 * Live-subscribe to invoices by status, for the approver dashboard.
 *
 * `'submitted'` = the FIFO queue (oldest first).
 * `'approved' | 'rejected'` = finalized invoices (newest first).
 * `'all'` = everything visible to approvers (newest first).
 */
export function useInvoicesByStatus(filter: ApprovalFilter): ListResult {
  const [state, setState] = useState<ListResult>({
    status: 'loading',
    invoices: [],
  });

  useEffect(() => {
    const baseRef = collection(db, COLLECTION);
    let q;
    if (filter === 'submitted') {
      q = query(
        baseRef,
        where('status', '==', 'submitted'),
        orderBy('createdAt', 'asc'),
      );
    } else if (filter === 'approved') {
      q = query(
        baseRef,
        where('status', '==', 'approved'),
        orderBy('createdAt', 'desc'),
      );
    } else if (filter === 'rejected') {
      q = query(
        baseRef,
        where('status', '==', 'rejected'),
        orderBy('createdAt', 'desc'),
      );
    } else {
      // 'all' — everything, newest first
      q = query(baseRef, orderBy('createdAt', 'desc'));
    }

    const unsub = onSnapshot(
      q,
      (snap) => {
        const invoices: InvoiceSubmission[] = [];
        snap.forEach((d) => {
          invoices.push({ ...(d.data() as InvoiceSubmission), id: d.id });
        });
        setState({ status: 'ready', invoices });
      },
      (err) => {
        // eslint-disable-next-line no-console
        console.error('[useInvoicesByStatus] snapshot error:', err);
        setState({ status: 'error', invoices: [], error: err.message });
      },
    );
    return unsub;
  }, [filter]);

  return state;
}
